import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCapsule } from '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';
import type { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import { KICK_COOLDOWN, PLAYER_SPEED, PLAYER_SPRINT_SPEED } from '../config/GameConfig';
import type { InputFrame } from '../systems/InputSystem';
import { rotateToward } from '../utils/DirectionalMath';
import { RiggedAdventurer } from './RiggedAdventurer';

function material(scene: Scene, name: string, color: Color3, emissive = Color3.Black()): StandardMaterial {
  const result = new StandardMaterial(name, scene);
  result.diffuseColor = color;
  result.emissiveColor = emissive;
  result.specularColor = new Color3(0.08, 0.08, 0.08);
  return result;
}

interface LegRig {
  hip: TransformNode;
  knee: TransformNode;
}

export class Player {
  readonly collider: Mesh;
  readonly visual: TransformNode;
  private readonly leftLeg: TransformNode;
  private readonly rightLeg: TransformNode;
  private readonly leftKnee: TransformNode;
  private readonly rightKnee: TransformNode;
  private readonly leftArm: TransformNode;
  private readonly rightArm: TransformNode;
  private readonly scarfTail: Mesh;
  private readonly ponytail: TransformNode;
  private readonly rigged: RiggedAdventurer;
  private animationTime = 0;
  private kickTimer = 0;
  private lastFootstepPhase = 0;
  private moving = false;
  private stumbleTimer = 0;
  private victoryTimer = 0;

  constructor(scene: Scene, start: { x: number; z: number }) {
    this.collider = CreateCapsule('player-collider', { height: 2.05, radius: 0.43, tessellation: 12 }, scene);
    this.collider.position.set(start.x, 1.04, start.z);
    // Face the chamber on spawn so the first gameplay view matches the title-art over-the-shoulder pose.
    this.collider.rotation.y = Math.atan2(-start.x, -start.z);
    this.collider.ellipsoid = new Vector3(0.43, 1.0, 0.43);
    this.collider.ellipsoidOffset = Vector3.Zero();
    this.collider.checkCollisions = true;
    this.collider.isVisible = false;
    this.collider.isPickable = false;
    this.collider.metadata = { isPlayer: true };
    const colliderDebugMaterial = material(scene, 'player-collider-debug', Color3.FromHexString('#5CE6FF'));
    colliderDebugMaterial.alpha = 0.16;
    colliderDebugMaterial.wireframe = true;
    colliderDebugMaterial.disableDepthWrite = true;
    this.collider.material = colliderDebugMaterial;

    this.visual = new TransformNode('adventurer', scene);
    this.visual.parent = this.collider;
    this.visual.position.y = -1.03;

    const skin = material(scene, 'adventurer-skin', Color3.FromHexString('#C97852'));
    const skinLight = material(scene, 'adventurer-skin-light', Color3.FromHexString('#E3A074'));
    const blush = material(scene, 'adventurer-blush', Color3.FromHexString('#C66D61'));
    const eyeWhite = material(scene, 'adventurer-eye-white', Color3.FromHexString('#FFF7E8'));
    const teal = material(scene, 'adventurer-teal', Color3.FromHexString('#087D7B'));
    const tealLight = material(scene, 'adventurer-teal-light', Color3.FromHexString('#23A7A0'));
    const tealDark = material(scene, 'adventurer-teal-dark', Color3.FromHexString('#073F46'));
    const scarf = material(scene, 'adventurer-scarf', Color3.FromHexString('#D85628'));
    scarf.backFaceCulling = false;
    scarf.twoSidedLighting = true;
    scarf.emissiveColor = Color3.FromHexString('#321006');
    const hair = material(scene, 'adventurer-hair', Color3.FromHexString('#27151C'));
    const hairLight = material(scene, 'adventurer-hair-highlight', Color3.FromHexString('#4A2725'));
    const leather = material(scene, 'adventurer-leather', Color3.FromHexString('#513128'));
    const boot = material(scene, 'adventurer-boots', Color3.FromHexString('#33211F'));
    const gold = material(scene, 'adventurer-gold', Color3.FromHexString('#DBA63A'), Color3.FromHexString('#241705'));

    // A layered outfit and smaller head create the more human silhouette used in the title artwork.
    const tunicSkirt = CreateCylinder('tunic-skirt', {
      height: 0.7, diameterTop: 0.62, diameterBottom: 0.86, tessellation: 16,
    }, scene);
    tunicSkirt.position.y = 1.21;
    tunicSkirt.material = teal;
    tunicSkirt.parent = this.visual;

    const tunicHem = CreateCylinder('tunic-gold-hem', {
      height: 0.065, diameterTop: 0.83, diameterBottom: 0.86, tessellation: 16,
    }, scene);
    tunicHem.position.y = 0.875;
    tunicHem.material = gold;
    tunicHem.parent = this.visual;

    const torso = CreateCylinder('tunic-bodice', {
      height: 0.82, diameterTop: 0.69, diameterBottom: 0.57, tessellation: 16,
    }, scene);
    torso.position.y = 1.75;
    torso.material = teal;
    torso.parent = this.visual;

    const chestPanel = CreateBox('tunic-chest-panel', { width: 0.33, height: 0.58, depth: 0.025 }, scene);
    chestPanel.position.set(0, 1.75, 0.335);
    chestPanel.material = tealLight;
    chestPanel.parent = this.visual;

    const belt = CreateCylinder('belt', { height: 0.12, diameter: 0.64, tessellation: 16 }, scene);
    belt.position.y = 1.42;
    belt.material = leather;
    belt.parent = this.visual;

    const buckle = CreateBox('buckle', { width: 0.16, height: 0.14, depth: 0.055 }, scene);
    buckle.position.set(0, 1.42, 0.335);
    buckle.material = gold;
    buckle.parent = this.visual;

    const shoulderStrap = CreateBox('satchel-strap', { width: 0.065, height: 0.94, depth: 0.045 }, scene);
    shoulderStrap.position.set(-0.02, 1.72, -0.32);
    shoulderStrap.rotation.z = -0.53;
    shoulderStrap.material = leather;
    shoulderStrap.parent = this.visual;

    const pouch = CreateBox('belt-pouch', { width: 0.3, height: 0.37, depth: 0.22 }, scene);
    pouch.position.set(0.41, 1.23, -0.04);
    pouch.rotation.z = -0.12;
    pouch.material = leather;
    pouch.parent = this.visual;
    const pouchFlap = CreateBox('belt-pouch-flap', { width: 0.31, height: 0.12, depth: 0.235 }, scene);
    pouchFlap.position.set(0.41, 1.39, -0.04);
    pouchFlap.rotation.z = -0.12;
    pouchFlap.material = boot;
    pouchFlap.parent = this.visual;

    const neck = CreateCylinder('adventurer-neck', { height: 0.23, diameter: 0.2, tessellation: 12 }, scene);
    neck.position.y = 2.15;
    neck.material = skin;
    neck.parent = this.visual;

    const head = CreateSphere('adventurer-head', { diameter: 0.55, segments: 24 }, scene);
    head.scaling.set(0.87, 1.05, 0.9);
    head.position.set(0, 2.49, 0.05);
    head.material = skinLight;
    head.parent = this.visual;

    const hairCap = CreateSphere('hair-cap', { diameter: 0.59, segments: 20 }, scene);
    hairCap.scaling.set(0.94, 1.06, 0.9);
    hairCap.position.set(0, 2.55, -0.025);
    hairCap.material = hair;
    hairCap.parent = this.visual;

    // The face sits slightly forward of the full hair mass, leaving a natural hairline and back-of-head volume.
    const faceMask = CreateSphere('face-mask', { diameter: 0.53, segments: 22 }, scene);
    faceMask.scaling.set(0.82, 0.96, 0.77);
    faceMask.position.set(0, 2.47, 0.095);
    faceMask.material = skinLight;
    faceMask.parent = this.visual;

    for (const side of [-1, 1]) {
      const ear = CreateSphere(`adventurer-ear-${side}`, { diameter: 0.09, segments: 10 }, scene);
      ear.scaling.y = 1.28;
      ear.position.set(side * 0.25, 2.49, 0.035);
      ear.material = skin;
      ear.parent = this.visual;

      const sideLock = CreateCapsule(`hair-side-lock-${side}`, { height: 0.39, radius: 0.055, tessellation: 10 }, scene);
      sideLock.position.set(side * 0.22, 2.48, 0.065);
      sideLock.rotation.z = side * 0.11;
      sideLock.material = hair;
      sideLock.parent = this.visual;

      const eye = CreateSphere(`adventurer-eye-${side}`, { diameter: 0.082, segments: 12 }, scene);
      eye.scaling.set(1, 0.76, 0.45);
      eye.position.set(side * 0.09, 2.53, 0.305);
      eye.material = eyeWhite;
      eye.parent = this.visual;

      const iris = CreateSphere(`adventurer-iris-${side}`, { diameter: 0.045, segments: 10 }, scene);
      iris.scaling.z = 0.42;
      iris.position.set(side * 0.09, 2.53, 0.334);
      iris.material = tealDark;
      iris.parent = this.visual;

      const brow = CreateBox(`adventurer-brow-${side}`, { width: 0.1, height: 0.018, depth: 0.022 }, scene);
      brow.position.set(side * 0.09, 2.595, 0.306);
      brow.rotation.z = side * -0.08;
      brow.material = hair;
      brow.parent = this.visual;

      const cheek = CreateSphere(`adventurer-cheek-${side}`, { diameter: 0.054, segments: 8 }, scene);
      cheek.scaling.set(1.35, 0.55, 0.28);
      cheek.position.set(side * 0.145, 2.45, 0.302);
      cheek.material = blush;
      cheek.parent = this.visual;
    }

    const nose = CreateSphere('adventurer-nose', { diameter: 0.06, segments: 10 }, scene);
    nose.scaling.set(0.68, 0.95, 0.8);
    nose.position.set(0, 2.49, 0.33);
    nose.material = skin;
    nose.parent = this.visual;

    const mouth = CreateBox('adventurer-smile', { width: 0.09, height: 0.015, depth: 0.016 }, scene);
    mouth.position.set(0, 2.415, 0.31);
    mouth.material = blush;
    mouth.parent = this.visual;

    for (const [index, x] of [-0.085, 0, 0.09].entries()) {
      const fringe = CreateSphere(`hair-fringe-${index}`, { diameter: 0.21, segments: 12 }, scene);
      fringe.scaling.set(0.72, 0.34, 0.32);
      fringe.position.set(x, 2.7 - Math.abs(x) * 0.22, 0.253);
      fringe.rotation.z = (x + 0.015) * 1.7;
      fringe.material = index === 1 ? hairLight : hair;
      fringe.parent = this.visual;
    }

    this.ponytail = new TransformNode('ponytail-pivot', scene);
    this.ponytail.position.set(0, 2.72, -0.245);
    this.ponytail.parent = this.visual;
    const hairTie = CreateSphere('ponytail-tie', { diameter: 0.15, segments: 12 }, scene);
    hairTie.scaling.z = 0.7;
    hairTie.material = tealLight;
    hairTie.parent = this.ponytail;
    const ponyUpper = CreateSphere('ponytail-upper', { diameter: 0.38, segments: 16 }, scene);
    ponyUpper.scaling.set(0.72, 1.2, 0.68);
    ponyUpper.position.set(-0.05, -0.21, -0.22);
    ponyUpper.rotation.x = -0.25;
    ponyUpper.rotation.z = 0.12;
    ponyUpper.material = hair;
    ponyUpper.parent = this.ponytail;
    const ponyLower = CreateSphere('ponytail-lower', { diameter: 0.42, segments: 16 }, scene);
    ponyLower.scaling.set(0.6, 1.45, 0.58);
    ponyLower.position.set(-0.15, -0.55, -0.31);
    ponyLower.rotation.x = -0.2;
    ponyLower.rotation.z = 0.28;
    ponyLower.material = hairLight;
    ponyLower.parent = this.ponytail;

    const scarfCollar = CreateCylinder('scarf-collar', {
      height: 0.18, diameterTop: 0.55, diameterBottom: 0.62, tessellation: 16,
    }, scene);
    scarfCollar.position.y = 2.09;
    scarfCollar.material = scarf;
    scarfCollar.parent = this.visual;
    this.scarfTail = this.createCape(scene, scarf, gold);

    const leftLeg = this.createLeg(scene, 'left-leg', -0.19, tealDark, boot);
    const rightLeg = this.createLeg(scene, 'right-leg', 0.19, tealDark, boot);
    this.leftLeg = leftLeg.hip;
    this.rightLeg = rightLeg.hip;
    this.leftKnee = leftLeg.knee;
    this.rightKnee = rightLeg.knee;
    this.leftArm = this.createArm(scene, 'left-arm', -0.43, teal, skinLight, leather, gold);
    this.rightArm = this.createArm(scene, 'right-arm', 0.43, teal, skinLight, leather, gold);
    this.rigged = new RiggedAdventurer(scene, this.collider, () => this.visual.setEnabled(false));
  }

  get position(): Vector3 { return this.collider.position; }
  get yaw(): number { return this.collider.rotation.y; }
  get isKicking(): boolean { return this.kickTimer > 0; }
  get visualMode(): 'rigged' | 'procedural' { return this.rigged.isReady ? 'rigged' : 'procedural'; }

  setShadowGenerator(shadow?: ShadowGenerator): void { this.rigged.setShadowGenerator(shadow); }

  startKick(target?: Vector3): boolean {
    if (this.kickTimer > 0) return false;
    this.kickTimer = KICK_COOLDOWN;
    if (target) {
      const towardTarget = target.subtract(this.position);
      towardTarget.y = 0;
      if (towardTarget.lengthSquared() > 0.01) this.collider.rotation.y = Math.atan2(towardTarget.x, towardTarget.z);
    }
    return true;
  }

  stumble(): void { this.stumbleTimer = 0.52; }
  celebrate(): void { this.victoryTimer = 2.2; }

  update(
    deltaSeconds: number,
    input: InputFrame,
    cameraPosition: Vector3,
    floorHeight: (x: number, z: number) => number,
    onFootstep: (sprinting: boolean) => void,
  ): void {
    this.animationTime += deltaSeconds;
    this.kickTimer = Math.max(0, this.kickTimer - deltaSeconds);
    this.stumbleTimer = Math.max(0, this.stumbleTimer - deltaSeconds);
    this.victoryTimer = Math.max(0, this.victoryTimer - deltaSeconds);

    const forward = this.collider.position.subtract(cameraPosition);
    forward.y = 0;
    if (forward.lengthSquared() < 0.01) forward.set(0, 0, 1);
    forward.normalize();
    const right = new Vector3(forward.z, 0, -forward.x);
    const movement = forward.scale(input.moveY).add(right.scale(input.moveX));
    this.moving = movement.lengthSquared() > 0.005 && this.kickTimer <= 0.2;
    const speed = input.sprint ? PLAYER_SPRINT_SPEED : PLAYER_SPEED;
    if (this.moving) {
      movement.normalize().scaleInPlace(speed * deltaSeconds);
      this.collider.moveWithCollisions(movement);
      const desiredYaw = Math.atan2(movement.x, movement.z);
      this.collider.rotation.y = rotateToward(this.collider.rotation.y, desiredYaw, deltaSeconds * 10);
    }
    const desiredY = floorHeight(this.position.x, this.position.z) + 1.04;
    this.position.y += (desiredY - this.position.y) * Math.min(1, deltaSeconds * 12);

    const cadence = input.sprint ? 13 : 9;
    const phase = this.animationTime * cadence;
    const stride = this.moving ? Math.sin(phase) * (input.sprint ? 0.7 : 0.48) : 0;
    this.leftLeg.rotation.x = stride;
    this.rightLeg.rotation.x = -stride;
    this.leftKnee.rotation.x = Math.max(0, -stride) * 0.34;
    this.rightKnee.rotation.x = Math.max(0, stride) * 0.34;
    this.leftArm.rotation.x = -stride * 0.7;
    this.rightArm.rotation.x = stride * 0.7;
    this.visual.position.y = -1.03 + (this.moving ? Math.abs(Math.sin(phase)) * 0.055 : Math.sin(this.animationTime * 2) * 0.018);
    this.scarfTail.rotation.x = -0.06 - (this.moving ? 0.22 : 0.025) + Math.sin(this.animationTime * 5) * 0.045;
    this.scarfTail.rotation.z = Math.sin(this.animationTime * (this.moving ? 4.8 : 1.7)) * (this.moving ? 0.045 : 0.016);
    this.ponytail.rotation.x = -0.05 + Math.sin(this.animationTime * (this.moving ? 7.5 : 2.1)) * (this.moving ? 0.11 : 0.025);
    this.ponytail.rotation.z = Math.sin(this.animationTime * (this.moving ? 5.5 : 1.5)) * (this.moving ? 0.12 : 0.035);

    const stepPhase = Math.floor(phase / Math.PI);
    if (this.moving && stepPhase !== this.lastFootstepPhase) {
      this.lastFootstepPhase = stepPhase;
      onFootstep(input.sprint);
    }

    if (this.kickTimer > 0) {
      const progress = 1 - this.kickTimer / KICK_COOLDOWN;
      let hipAngle: number;
      let kneeAngle: number;
      let power: number;
      if (progress < 0.26) {
        const t = this.smoothStep(progress / 0.26);
        hipAngle = -0.25 * t;
        kneeAngle = 1.52 * t;
        power = t * 0.48;
      } else if (progress < 0.48) {
        const t = this.smoothStep((progress - 0.26) / 0.22);
        hipAngle = -0.25 - 0.25 * t;
        kneeAngle = 1.52 * (1 - t);
        power = 0.48 + t * 0.52;
      } else if (progress < 0.68) {
        hipAngle = -0.5;
        kneeAngle = 0;
        power = 1;
      } else {
        const t = this.smoothStep((progress - 0.68) / 0.32);
        hipAngle = -0.5 * (1 - t);
        kneeAngle = Math.sin(t * Math.PI) * 0.2;
        power = 1 - t;
      }
      // Turn the hip side-on, chamber the knee, snap the heel at the target, then recover into stance.
      this.rightLeg.rotation.x = hipAngle;
      this.rightLeg.rotation.z = 1.24 * power;
      this.rightKnee.rotation.x = kneeAngle;
      this.leftLeg.rotation.x = 0.08 * power;
      this.leftKnee.rotation.x = 0.22 * power;
      this.leftArm.rotation.x = -0.86 * power;
      this.rightArm.rotation.x = -1.02 * power;
      this.leftArm.rotation.z = -0.48 * power;
      this.rightArm.rotation.z = 0.58 * power;
      this.visual.rotation.x = 0.1 * power;
      this.visual.rotation.y = -1.05 * power;
      this.visual.rotation.z = -0.16 * power;
      this.scarfTail.rotation.x -= power * 0.22;
      this.ponytail.rotation.x += power * 0.18;
    } else {
      this.rightLeg.rotation.z *= Math.max(0, 1 - deltaSeconds * 14);
      this.visual.rotation.x *= Math.max(0, 1 - deltaSeconds * 14);
      this.visual.rotation.y *= Math.max(0, 1 - deltaSeconds * 12);
    }
    if (this.stumbleTimer > 0) {
      const reaction = Math.sin((0.52 - this.stumbleTimer) * 22) * this.stumbleTimer;
      this.visual.rotation.z = reaction * 0.65;
      this.leftArm.rotation.z = -0.7; this.rightArm.rotation.z = 0.7;
    } else if (this.victoryTimer > 0) {
      this.leftArm.rotation.z = -2.35; this.rightArm.rotation.z = 2.35;
      this.visual.rotation.y += deltaSeconds * 2.6;
      this.visual.position.y += Math.abs(Math.sin(this.animationTime * 7)) * 0.12;
    } else if (this.kickTimer <= 0) {
      this.leftArm.rotation.z *= Math.max(0, 1 - deltaSeconds * 12);
      this.rightArm.rotation.z *= Math.max(0, 1 - deltaSeconds * 12);
      this.visual.rotation.z *= Math.max(0, 1 - deltaSeconds * 12);
    }

    this.rigged.update({
      moving: this.moving,
      sprinting: input.sprint,
      kickProgress: this.kickTimer > 0 ? 1 - this.kickTimer / KICK_COOLDOWN : undefined,
      stumbleProgress: this.stumbleTimer > 0 ? 1 - this.stumbleTimer / 0.52 : undefined,
      celebrating: this.victoryTimer > 0,
    });
  }

  setDebugVisible(visible: boolean): void { this.collider.isVisible = visible; }

  dispose(): void { this.rigged.dispose(); this.collider.dispose(false, true); }

  private createArm(
    scene: Scene,
    name: string,
    x: number,
    sleeveMaterial: StandardMaterial,
    skinMaterial: StandardMaterial,
    wrapMaterial: StandardMaterial,
    trimMaterial: StandardMaterial,
  ): TransformNode {
    const pivot = new TransformNode(name, scene);
    pivot.position.set(x, 2.0, 0);
    pivot.parent = this.visual;

    const shoulder = CreateSphere(`${name}-shoulder`, { diameter: 0.255, segments: 14 }, scene);
    shoulder.scaling.y = 0.9;
    shoulder.material = sleeveMaterial;
    shoulder.parent = pivot;

    const sleeve = CreateCapsule(`${name}-sleeve`, { height: 0.34, radius: 0.105, tessellation: 12 }, scene);
    sleeve.position.y = -0.16;
    sleeve.material = sleeveMaterial;
    sleeve.parent = pivot;

    const sleeveTrim = CreateCylinder(`${name}-sleeve-trim`, { height: 0.05, diameter: 0.21, tessellation: 12 }, scene);
    sleeveTrim.position.y = -0.32;
    sleeveTrim.material = trimMaterial;
    sleeveTrim.parent = pivot;

    const forearm = CreateCapsule(`${name}-forearm`, { height: 0.5, radius: 0.074, tessellation: 12 }, scene);
    forearm.position.y = -0.51;
    forearm.material = skinMaterial;
    forearm.parent = pivot;

    const wristWrap = CreateCylinder(`${name}-wrist-wrap`, { height: 0.14, diameter: 0.18, tessellation: 12 }, scene);
    wristWrap.position.y = -0.68;
    wristWrap.material = wrapMaterial;
    wristWrap.parent = pivot;

    const hand = CreateSphere(`${name}-hand`, { diameter: 0.165, segments: 12 }, scene);
    hand.scaling.set(0.85, 1.2, 0.7);
    hand.position.y = -0.83;
    hand.material = skinMaterial;
    hand.parent = pivot;
    return pivot;
  }

  private createLeg(
    scene: Scene,
    name: string,
    x: number,
    leggingsMaterial: StandardMaterial,
    bootMaterial: StandardMaterial,
  ): LegRig {
    const pivot = new TransformNode(name, scene);
    pivot.position.set(x, 1.22, 0);
    pivot.parent = this.visual;

    const thigh = CreateCapsule(`${name}-thigh`, { height: 0.62, radius: 0.12, tessellation: 12 }, scene);
    thigh.position.y = -0.28;
    thigh.material = leggingsMaterial;
    thigh.parent = pivot;

    const kneePivot = new TransformNode(`${name}-knee-pivot`, scene);
    kneePivot.position.y = -0.56;
    kneePivot.parent = pivot;

    const knee = CreateSphere(`${name}-knee`, { diameter: 0.23, segments: 10 }, scene);
    knee.scaling.set(0.9, 1, 0.92);
    knee.material = leggingsMaterial;
    knee.parent = kneePivot;

    const bootShaft = CreateCapsule(`${name}-boot-shaft`, { height: 0.52, radius: 0.115, tessellation: 12 }, scene);
    bootShaft.position.y = -0.25;
    bootShaft.material = bootMaterial;
    bootShaft.parent = kneePivot;

    const foot = CreateSphere(`${name}-boot`, { diameter: 0.27, segments: 12 }, scene);
    foot.scaling.set(0.9, 0.7, 1.62);
    foot.position.set(0, -0.51, 0.095);
    foot.material = bootMaterial;
    foot.parent = kneePivot;
    return { hip: pivot, knee: kneePivot };
  }

  private smoothStep(value: number): number {
    const t = Math.max(0, Math.min(1, value));
    return t * t * (3 - 2 * t);
  }

  private createCape(scene: Scene, capeMaterial: StandardMaterial, trimMaterial: StandardMaterial): Mesh {
    const cape = new Mesh('scarf-cape', scene);
    const positions = [
      -0.34, 0, 0,       0, 0, -0.025,       0.34, 0, 0,
      -0.46, -0.42, -0.1,  -0.01, -0.4, -0.15,  0.43, -0.42, -0.1,
      -0.58, -0.95, -0.18, -0.06, -0.91, -0.24, 0.38, -0.93, -0.17,
      -0.72, -1.38, -0.08, -0.17, -1.31, -0.18, 0.25, -1.23, -0.06,
    ];
    const indices: number[] = [];
    for (let row = 0; row < 3; row += 1) {
      for (let col = 0; col < 2; col += 1) {
        const a = row * 3 + col;
        const b = a + 1;
        const c = a + 3;
        const d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    const uvs: number[] = [];
    for (let row = 0; row < 4; row += 1) {
      for (let col = 0; col < 3; col += 1) uvs.push(col / 2, row / 3);
    }
    const data = new VertexData();
    data.positions = positions;
    data.indices = indices;
    data.normals = normals;
    data.uvs = uvs;
    data.applyToMesh(cape);
    cape.position.set(0, 2.05, -0.31);
    cape.material = capeMaterial;
    cape.parent = this.visual;
    cape.isPickable = false;

    // A narrow, slightly raised border echoes the woven gold motif on the title-screen scarf.
    const trim = new Mesh('scarf-cape-trim', scene);
    const trimPositions = [
      -0.34, 0, 0.012, -0.295, -0.005, 0.014,
      -0.46, -0.42, -0.088, -0.415, -0.41, -0.086,
      -0.58, -0.95, -0.168, -0.532, -0.935, -0.166,
      -0.72, -1.38, -0.068, -0.668, -1.355, -0.066,
      -0.17, -1.31, -0.168, -0.16, -1.26, -0.166,
      0.25, -1.23, -0.048, 0.245, -1.18, -0.046,
    ];
    const trimIndices = [
      0, 2, 1, 1, 2, 3,
      2, 4, 3, 3, 4, 5,
      4, 6, 5, 5, 6, 7,
      6, 8, 7, 7, 8, 9,
      8, 10, 9, 9, 10, 11,
    ];
    const trimNormals: number[] = [];
    VertexData.ComputeNormals(trimPositions, trimIndices, trimNormals);
    const trimData = new VertexData();
    trimData.positions = trimPositions;
    trimData.indices = trimIndices;
    trimData.normals = trimNormals;
    trimData.applyToMesh(trim);
    trimMaterial.backFaceCulling = false;
    trim.material = trimMaterial;
    trim.parent = cape;
    trim.isPickable = false;
    return cape;
  }
}
